/**
 * v2.0.0 の評価データマッパー
 *
 * データ構造 (functions/src/api/app/userOpinionEvaluationV2_0_0.ts と一致):
 * - evaluationVersion: "2.0.0"
 * - topic: { id, theme, background, genre, difficulty }
 * - questionType / claimType はルート直下
 * - input: { userOpinion, understanding? }
 * - topicContent: ルート直下（旧 topic.content 相当、任意）
 * - app_version
 * - result: { understanding, logicOriginality, structureExpression, improvement, totalScore, questionType, claimType }
 */

import { DocumentData } from "firebase/firestore";
import { EvaluationListItem, EvaluationDetailV2_0_0, QuestionType, ClaimType } from "../../types";
import { getTimestampSeconds } from "./utils";

/**
 * v2.0.0データを一覧アイテムに変換
 */
export function mapV2_0_0ToListItem(docId: string, data: DocumentData): EvaluationListItem {
  const userOpinion = data.input?.userOpinion || "";
  const theme = data.topic?.theme || "";
  const topicContent = data.topicContent || theme;

  return {
    id: docId,
    version: "v2.0.0",
    timestamp: { _seconds: getTimestampSeconds(data.timestamp) },
    topic: {
      id: data.topic?.id || "",
      content: topicContent,
      genre: data.topic?.genre || "",
      difficulty: data.topic?.difficulty || 0,
      theme,
      questionType: (data.questionType as QuestionType) || undefined,
      claimType: (data.claimType as ClaimType) || undefined,
      background: data.topic?.background || undefined,
    },
    userOpinionPreview:
      userOpinion.length > 100 ? userOpinion.substring(0, 100) + "..." : userOpinion,
    totalScore: data.result?.totalScore || 0,
    appVersion: data.app_version || "",
  };
}

/**
 * v2.0.0データを詳細に変換
 */
export function mapV2_0_0ToDetail(docId: string, data: DocumentData): EvaluationDetailV2_0_0 {
  const resultData = data.result || {};
  const understandingBlock = resultData.understanding || {};
  const logicOriginalityBlock = resultData.logicOriginality || {};
  const structureExpressionBlock = resultData.structureExpression || {};
  const improvementBlock = resultData.improvement || {};

  const understanding =
    data.input?.understanding != null
      ? {
          claim: data.input.understanding.claim || "",
          grounds: data.input.understanding.grounds || "",
        }
      : undefined;

  return {
    id: docId,
    version: "v2.0.0",
    timestamp: { _seconds: getTimestampSeconds(data.timestamp) },
    topic: {
      id: data.topic?.id || "",
      theme: data.topic?.theme || "",
      background: data.topic?.background || undefined,
      genre: data.topic?.genre || "",
      difficulty: data.topic?.difficulty || 0,
      content: data.topicContent || undefined,
      questionType: (data.questionType as QuestionType) || undefined,
      claimType: (data.claimType as ClaimType | null) ?? undefined,
    },
    input: {
      userOpinion: data.input?.userOpinion || "",
      understanding,
    },
    result: {
      understanding: {
        scores: {
          understanding: understandingBlock.scores?.understanding || 0,
        },
        scoreExplanations: {
          understanding: understandingBlock.scoreExplanations?.understanding || "",
        },
        reasoning: understandingBlock.reasoning || undefined,
      },
      logicOriginality: {
        scores: {
          logic: logicOriginalityBlock.scores?.logic || 0,
          originality: logicOriginalityBlock.scores?.originality || 0,
        },
        scoreExplanations: {
          logic: logicOriginalityBlock.scoreExplanations?.logic || "",
          originality: logicOriginalityBlock.scoreExplanations?.originality || "",
        },
        reasoning: logicOriginalityBlock.reasoning || undefined,
      },
      structureExpression: {
        scores: {
          structure: structureExpressionBlock.scores?.structure || 0,
          expression: structureExpressionBlock.scores?.expression || 0,
        },
        scoreExplanations: {
          structure: structureExpressionBlock.scoreExplanations?.structure || "",
          expression: structureExpressionBlock.scoreExplanations?.expression || "",
        },
        reasoning: structureExpressionBlock.reasoning || undefined,
      },
      improvement: {
        shortReview: improvementBlock.shortReview || "",
        improvements: improvementBlock.improvements || [],
        strengths: improvementBlock.strengths || [],
        improvedExample: improvementBlock.improvedExample || "",
      },
      questionType: (resultData.questionType as QuestionType) || undefined,
      claimType: (resultData.claimType as ClaimType | null) ?? undefined,
      totalScore: resultData.totalScore || 0,
    },
    appVersion: data.app_version || "",
    promptVersion: typeof data.promptVersion === "number" ? data.promptVersion : undefined,
  };
}
